/**
 * Module Registry - Singleton
 *
 * @version 1.0
 * @module core/module-registry
 *
 * @description
 * Registro central de módulos de la plataforma.
 * Permite que cada módulo se registre dinámicamente sin modificar el core.
 *
 * @remarks
 * **Patrón de uso**:
 * 1. Cada módulo llama a `moduleRegistry.register()` en su index.ts
 * 2. MapLayout y BottomToolbar consultan el registro para renderizar
 * 3. No hay código hardcoded por módulo en el core
 *
 * @example
 * ```typescript
 * // En modules/waste/index.ts
 * moduleRegistry.register({
 *   id: 'waste',
 *   leftPanel: WasteTabbedPanel,
 *   toolbar: { content: WasteToolbarContent },
 *   provider: WasteProvider,
 * });
 *
 * // En MapLayout.tsx
 * const module = moduleRegistry.get(activeModule);
 * if (module) {
 *   const { leftPanel: Panel, provider: Provider } = module;
 *   // Renderizar...
 * }
 * ```
 */

import type { ModuleId } from '../module-store/types';
import type { ModuleDefinition, ModuleRegistryListener } from './types';

/**
 * Clase ModuleRegistry
 *
 * @remarks
 * Implementa el patrón Singleton para garantizar un único registro global.
 * Soporta suscripciones para notificar cambios a componentes React.
 */
class ModuleRegistry {
  /**
   * Mapa de módulos registrados
   */
  private modules: Map<ModuleId, ModuleDefinition> = new Map();

  /**
   * Set de listeners para notificar cambios
   */
  private listeners: Set<ModuleRegistryListener> = new Set();

  /**
   * Versión del estado (para detectar cambios)
   */
  private version = 0;

  /**
   * Registra un módulo en el sistema
   *
   * @param definition - Definición completa del módulo
   *
   * @remarks
   * Si el módulo ya existe, se sobrescribe (útil para hot reload).
   * Notifica a todos los listeners después del registro.
   *
   * @example
   * ```typescript
   * moduleRegistry.register({
   *   id: 'waste',
   *   leftPanel: WasteTabbedPanel,
   *   toolbar: { content: WasteToolbarContent },
   *   provider: WasteProvider,
   * });
   * ```
   */
  register(definition: ModuleDefinition): void {
    const { id } = definition;

    // Validar ID
    if (!id) {
      console.error('[ModuleRegistry] Cannot register module without id');
      return;
    }

    // Registrar o sobrescribir
    const isUpdate = this.modules.has(id);
    this.modules.set(id, definition);
    this.version++;

    // Notificar listeners
    this.notifyListeners();

    console.debug(
      `[ModuleRegistry] ${isUpdate ? 'Updated' : 'Registered'} module: ${id}`,
      {
        hasLeftPanel: !!definition.leftPanel,
        hasToolbar: !!definition.toolbar,
        hasProvider: !!definition.provider,
        requiresRealtime: definition.requiresRealtime,
      }
    );
  }

  /**
   * Elimina un módulo del registro
   *
   * @param moduleId - ID del módulo a eliminar
   *
   * @returns true si se eliminó, false si no existía
   */
  unregister(moduleId: ModuleId): boolean {
    const existed = this.modules.delete(moduleId);

    if (existed) {
      this.version++;
      this.notifyListeners();
      console.debug(`[ModuleRegistry] Unregistered module: ${moduleId}`);
    }

    return existed;
  }

  /**
   * Obtiene la definición de un módulo
   *
   * @param moduleId - ID del módulo
   * @returns Definición del módulo o undefined si no existe
   */
  get(moduleId: ModuleId): ModuleDefinition | undefined {
    return this.modules.get(moduleId);
  }

  /**
   * Verifica si un módulo está registrado
   *
   * @param moduleId - ID del módulo
   */
  has(moduleId: ModuleId): boolean {
    return this.modules.has(moduleId);
  }

  /**
   * Obtiene todos los módulos registrados
   *
   * @returns Array con todas las definiciones
   */
  getAll(): ModuleDefinition[] {
    return Array.from(this.modules.values());
  }

  /**
   * Obtiene los IDs de todos los módulos registrados
   *
   * @returns Array de IDs
   */
  getRegisteredIds(): ModuleId[] {
    return Array.from(this.modules.keys());
  }

  /**
   * Obtiene el número de módulos registrados
   */
  get size(): number {
    return this.modules.size;
  }

  /**
   * Obtiene la versión actual del registro
   *
   * @remarks
   * Útil para detectar cambios sin suscribirse
   */
  getVersion(): number {
    return this.version;
  }

  /**
   * Suscribe un listener para cambios en el registro
   *
   * @param listener - Función a llamar cuando cambie el registro
   * @returns Función para desuscribirse
   *
   * @example
   * ```typescript
   * useEffect(() => {
   *   return moduleRegistry.subscribe(() => {
   *     // Re-renderizar cuando cambien los módulos
   *     forceUpdate();
   *   });
   * }, []);
   * ```
   */
  subscribe(listener: ModuleRegistryListener): () => void {
    this.listeners.add(listener);

    // Retornar función de cleanup
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notifica a todos los listeners de un cambio
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        console.error('[ModuleRegistry] Error in listener:', error);
      }
    });
  }

  /**
   * Limpia todo el registro (útil para tests)
   */
  clear(): void {
    this.modules.clear();
    this.version++;
    this.notifyListeners();
    console.debug('[ModuleRegistry] Cleared all modules');
  }
}

/**
 * Instancia singleton del registro de módulos
 *
 * @example
 * ```typescript
 * import { moduleRegistry } from '@/core/module-registry';
 *
 * // Registrar módulo
 * moduleRegistry.register({ id: 'waste', ... });
 *
 * // Obtener módulo
 * const module = moduleRegistry.get('waste');
 * ```
 */
export const moduleRegistry = new ModuleRegistry();
